import {
  createClient,
  createAdminClient,
  type HttpClient,
  HttpError,
} from "@sdk/core";
import { getAccessToken, getTenantId, setAccessToken } from "./auth";
import { isSensitiveMessage, parseApiError } from "./errors";

import { loadAuth, saveAuth } from "./tokenStorage";
import { translateUi } from "@i18n/config";
const processEnv =
  typeof globalThis !== "undefined" &&
  typeof (globalThis as any).process !== "undefined"
    ? (globalThis as any).process.env
    : undefined;

function resolveFromProcessEnv(
  key:
    | "VITE_API_BASE_URL"
    | "VITE_SDK_BASE_URL"
    | "API_BASE_URL"
    | "NEXT_PUBLIC_API_BASE_URL"
    | "VITE_API_REQUEST_TIMEOUT_MS"
    | "API_REQUEST_TIMEOUT_MS"
    | "NEXT_PUBLIC_API_REQUEST_TIMEOUT_MS",
) {
  if (!processEnv) return undefined;
  const value = processEnv[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function getApiBaseUrl(): string {
  if (
    typeof window !== "undefined" &&
    (window as any).__VITE_API_BASE_URL_OVERRIDE__
  ) {
    return (window as any).__VITE_API_BASE_URL_OVERRIDE__;
  }
  const candidate =
    import.meta.env.VITE_API_BASE_URL ||
    import.meta.env.VITE_SDK_BASE_URL ||
    resolveFromProcessEnv("VITE_API_BASE_URL") ||
    resolveFromProcessEnv("VITE_SDK_BASE_URL") ||
    resolveFromProcessEnv("NEXT_PUBLIC_API_BASE_URL") ||
    resolveFromProcessEnv("API_BASE_URL");

  const resolved = candidate || "/api";
  return resolved;
}

function getAdminRequestTimeoutMs(): number {
  const raw =
    (import.meta.env as any).VITE_API_REQUEST_TIMEOUT_MS ||
    (import.meta.env as any).NEXT_PUBLIC_API_REQUEST_TIMEOUT_MS ||
    resolveFromProcessEnv("VITE_API_REQUEST_TIMEOUT_MS") ||
    resolveFromProcessEnv("NEXT_PUBLIC_API_REQUEST_TIMEOUT_MS") ||
    resolveFromProcessEnv("API_REQUEST_TIMEOUT_MS");
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 20_000;
}

export function makeApiUrl(path: string): string {
  const base = getApiBaseUrl().replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (!base || base === "/") return normalizedPath;

  if (/^https?:\/\//i.test(base)) {
    try {
      return new URL(normalizedPath, base).toString();
    } catch {
      return `${base}${normalizedPath}`;
    }
  }

  if (normalizedPath.startsWith(base)) return normalizedPath;
  return `${base}${normalizedPath}`;
}

function getRefreshToken(): string | null {
  const stored = loadAuth();
  return stored?.refreshToken ?? null;
}

function onTokensRefreshed(tokens: {
  accessToken: string;
  refreshToken?: string | null;
  expiresAt?: number | null;
  user?: unknown;
}) {
  try {
    const current = loadAuth() || {
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
    };
    const merged = {
      ...current,
      accessToken: tokens.accessToken ?? current.accessToken,
      refreshToken:
        typeof tokens.refreshToken !== "undefined"
          ? tokens.refreshToken
          : current.refreshToken,
      expiresAt:
        typeof tokens.expiresAt !== "undefined"
          ? tokens.expiresAt
          : current.expiresAt,
      user:
        typeof tokens.user !== "undefined"
          ? (tokens.user as any)
          : current.user,
    };
    saveAuth(merged);
    setAccessToken(merged.accessToken);
  } catch {
    // ignore storage issues
  }
}

// Underlying SDK client
let baseUrl = getApiBaseUrl();
if (!(typeof process !== "undefined" && (process as any).env?.VITEST)) {
  console.info(`[adminHttp] Using API base URL: ${baseUrl}`);
}
const sdkClient = createClient({
  baseUrl: baseUrl,
  getToken: getAccessToken,
  getTenantId,
  getRefreshToken,
  onTokensRefreshed,
  timeoutMs: getAdminRequestTimeoutMs(),
  // refreshPath defaults to '/auth/refresh'
});

// Optional single-shot header debug logging
let loggedOnce = false;
function shouldDebug(): boolean {
  try {
    // window/localStorage toggles
    if (typeof window !== "undefined") {
      const w: any = window as any;
      if (typeof w.__STORIZONE_DEBUG_HTTP__ !== "undefined")
        return !!w.__STORIZONE_DEBUG_HTTP__;
      const v = window.localStorage.getItem("storizone:debug:http");
      if (v) return v === "1" || v.toLowerCase() === "true";
    }
  } catch {}
  try {
    const env: any =
      typeof process !== "undefined" && (process as any).env
        ? (process as any).env
        : {};
    const val = env.DEBUG_HTTP || env.VITE_DEBUG_HTTP;
    if (typeof val !== "undefined")
      return String(val) === "1" || String(val).toLowerCase() === "true";
  } catch {}
  return false;
}

function logPlannedHeadersOnce(method: string, path: string) {
  if (loggedOnce || !shouldDebug()) return;
  loggedOnce = true;
  try {
    const token = getAccessToken?.() ?? loadAuth()?.accessToken ?? null;
    const tenant = getTenantId?.() ?? null;
    // eslint-disable-next-line no-console
    console.info("[adminHttp] First request", {
      method,
      path,
      Authorization: token ? "Bearer <redacted>" : "<none>",
      "X-Tenant-Id": tenant ?? "<none>",
    });
  } catch {}
}

function offlineRequest<T>(): Promise<T> {
  return Promise.reject(new Error(translateUi("internetConnectionRequired", "Internet connection required. Only POS sales can be saved offline.")));
}

async function handleErrors<T>(p: Promise<T>): Promise<T> {
  try {
    return await p;
  } catch (err: any) {
    // Attempt to extract friendly message from ProblemDetails (RFC 7807)
    if ((err instanceof HttpError || err?.name === "HttpError") && err.body) {
      try {
        const data = JSON.parse(err.body);
        if (data && typeof data === "object") {
          // Structured error payloads { code, message, details }
          if (data.message && !isSensitiveMessage(data.message)) {
            err.message = data.message;
          } else if (data.error && !isSensitiveMessage(data.error)) {
            err.message = data.error;
          } else if (data.detail && !isSensitiveMessage(data.detail)) {
            // Standard ProblemDetails 'detail'
            err.message = data.detail;
          } else if (data.errors && typeof data.errors === "object") {
            // ValidationProblemDetails 'errors'
            // e.g. { errors: { "Field": ["Error"] } }
            const firstKey = Object.keys(data.errors)[0];
            if (firstKey) {
              const firstMsg = Array.isArray(data.errors[firstKey])
                ? data.errors[firstKey][0]
                : data.errors[firstKey];
              const composed = `${firstKey}: ${firstMsg}`;
              if (!isSensitiveMessage(composed)) err.message = composed;
            }
          }
        }
      } catch {
        // Body is not JSON, keep original message
      }
    }

    const parsed = parseApiError(err);
    if (err && typeof err === "object") {
      try {
        err.message = parsed.message;
        if (
          typeof parsed.status === "number" &&
          typeof err.status !== "number"
        ) {
          err.status = parsed.status;
        }
        if (parsed.code && typeof err.code !== "string") {
          err.code = parsed.code;
        }
        if (parsed.correlationId && !err.correlationId) {
          err.correlationId = parsed.correlationId;
        }
        if (typeof parsed.retryable === "boolean") {
          err.retryable = parsed.retryable;
        }
        if (parsed.errors) {
          err.errors = parsed.errors;
        }
        if (parsed.slugConflict) {
          err.slugConflict = parsed.slugConflict;
        }
      } catch {
        // Keep the original error object if it cannot be mutated.
      }
    }

    // Check for 403 Forbidden
    // if (err instanceof HttpError && err.status === 403) {
    // 403 means Forbidden (valid token, no permission). We shouldn't logout.
    // Let the caller handle it.
    // }
    throw err;
  }
}

// Wrap to inject debug logging before delegating
export const adminHttp: HttpClient = {
  get: <T = unknown>(
    path: string,
    options?: Parameters<HttpClient["get"]>[1],
  ) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return offlineRequest<T>();
    logPlannedHeadersOnce("GET", path);
    return handleErrors(sdkClient.get<T>(path, options));
  },
  post: <T = unknown>(
    path: string,
    json?: any,
    options?: Parameters<HttpClient["post"]>[2],
  ) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return offlineRequest<T>();
    logPlannedHeadersOnce("POST", path);
    return handleErrors(sdkClient.post<T>(path, json, options));
  },
  patch: <T = unknown>(
    path: string,
    json?: any,
    options?: Parameters<HttpClient["patch"]>[2],
  ) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return offlineRequest<T>();
    logPlannedHeadersOnce("PATCH", path);
    return handleErrors(sdkClient.patch<T>(path, json, options));
  },
  put: <T = unknown>(
    path: string,
    json?: any,
    options?: Parameters<HttpClient["put"]>[2],
  ) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return offlineRequest<T>();
    logPlannedHeadersOnce("PUT", path);
    return handleErrors(sdkClient.put<T>(path, json, options));
  },
  delete: <T = unknown>(
    path: string,
    json?: any,
    options?: Parameters<HttpClient["delete"]>[2],
  ) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return offlineRequest<T>();
    logPlannedHeadersOnce("DELETE", path);
    return handleErrors(sdkClient.delete<T>(path, json, options));
  },
};

// Convenience Admin SDK facade using shared client
export const adminSdk = createAdminClient(adminHttp as any) as any;
